/**
 * @file OrderItemPicker.js
 * @version 1.10.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-13
 * @modified 2026-10-30
 * @description The Item cell of the lines grid: pick an inventory item,
 *              create one, narrow to what this customer (or supplier) has traded
 *              before, or deliberately have none.
 *
 *              `party` says which side of the trade the document is on. On a
 *              customer document (order, invoice) the side panel shows what the
 *              customer has ordered and what they are contracted for. On a
 *              supplier document (purchase order, bill) there are no customer
 *              contracts to show: the panel shows what has been bought from this
 *              supplier and, on a bill, the lines of the purchase orders it is
 *              linked to — which is usually exactly what is being billed.
 *
 *              Not built on SearchableDropdown, for two structural reasons.
 *              Its `.dropdown-menu` is a single flex column pinned to the
 *              trigger's width with `overflow: hidden`, so there is nowhere to
 *              put a second, separately-scrolling panel — and its CSS is shared
 *              with ComboBox, so widening it would reach every dropdown in the
 *              app. Second, the three actions at the top have to be FIXED: the
 *              file this replaces faked such rows as sentinel options twice, and
 *              a sentinel option scrolls away with the list and is filtered out
 *              by the first thing anyone types.
 *
 *              Keyboard navigation is deliberate. No dropdown in this client has
 *              any — SearchableDropdown, Dropdown, ComboBox and
 *              MultiSelectDropdown have no key handlers between them — and this
 *              one sits in a grid built for fast repeated data entry.
 *
 *              1.9.0 (2026-10-28, Cam — "shows archived items in the recent
 *              orders side, but should be faded and with an archived badge"):
 *              a side-panel row whose product is archived is faded, badged
 *              "Archived" and cannot be chosen (aria-disabled, out of the Tab
 *              order, titled "Archived — choose the product that replaces
 *              it") — except the line's own item, and a bill's "On linked POs"
 *              rows, which stay choosable (utils/pickerArchived). A code held by
 *              an active and an archived item is the active one.
 *
 *              1.10.0 (2026-10-30, Cam — "add a X button aligned right to each
 *              item … that shows previously ordered items, so users can remove
 *              items from that list, and items are removed for everyone, and
 *              only join the list again if/when the removed item is ordered
 *              again"): with `onRemovePrevious`, every "Recently ordered" /
 *              "Recently bought" row has an X at its right (the line's own item
 *              keeps the space, empty). Greyed with the server's reason
 *              (`remove_block` on the row — not ready, view only, permission).
 *              The screen takes the row off at once and offers Undo
 *              (hooks/usePreviousItemRemoval). Not on "Contracted", "On linked
 *              POs" or the list on the left.
 */

import React, { useState, useRef, useEffect, useMemo, useCallback, useId } from 'react';
import useDropdownPosition from '../hooks/useDropdownPosition';
import ItemStatusBadge from './ItemStatusBadge';
import { itemForPickerRow, pickerRowState } from '../utils/pickerArchived';
import { pricingBadgesFor, contractAppliesOn, itemCodeOf, itemNameOf, additiveSignature, contractKeyOf } from '../utils/customerPricing';
import { withAdditives } from '../utils/additiveLabel';
import { formatDateShort } from '../utils/dateFormatters';
import { formatQuantity } from '../utils/numberFormatters';
import { REMOVE_TITLE } from '../utils/previousItemRemovals';
import './OrderItemPicker.css';

const NO_ITEM = '__no_item__';
const ADD_ITEM = '__add_item__';
const PREVIOUS = '__previous__';
const ARCHIVED = '__archived__';

/** Wording that depends on which side of the trade the document is on. */
const PARTY_TEXT = {
  customer: {
    toggle: 'Search customer’s previous items',
    toggleHint: 'Show only items this customer has bought before, most recent first',
    searchPrevious: 'Search this customer’s previous items…',
    noPrevious: 'This customer hasn’t bought anything from inventory before.',
    recent: 'Recently ordered',
    recentEmpty: 'Nothing ordered yet.',
  },
  supplier: {
    toggle: 'Search supplier’s previous items',
    toggleHint: 'Show only items bought from this supplier before, most recent first',
    searchPrevious: 'Search this supplier’s previous items…',
    noPrevious: 'Nothing has been bought from this supplier from inventory before.',
    recent: 'Recently bought',
    recentEmpty: 'Nothing bought from this supplier yet.',
  },
};

/**
 * One row of the side panel. An archived product that cannot be chosen stays
 * a button (so its title still shows on hover) but is aria-disabled, out of the
 * Tab order and does nothing when clicked.
 */
function SideRow({ state, title, disabled = false, onPick, children }) {
  const blocked = !!state?.blocked;
  return (
    <button
      type="button"
      className={`oip-side-row ${state?.archived ? 'is-archived' : ''}`.trim()}
      disabled={disabled}
      aria-disabled={blocked ? 'true' : undefined}
      tabIndex={blocked ? -1 : undefined}
      onClick={() => { if (!blocked) onPick?.(); }}
      title={[state?.title, title].filter(Boolean).join(' · ') || undefined}
    >
      {children}
    </button>
  );
}

/** A side row's name, with the item's status badge after it when it is not simply in use. */
function SideName({ name, item, archived = false }) {
  const shown = item && (archived || item.is_locally_archived || item.awaiting_supplier_code);
  return (
    <span className="oip-side-name-box">
      <span className="oip-side-name">{name}</span>
      {shown && (
        <ItemStatusBadge item={archived ? { ...item, is_archived: true } : item} className="oip-item-status" />
      )}
    </span>
  );
}

/**
 * The fixed rows above the list. Never filtered, never scrolled away.
 *
 * The archived toggle appears only where there is something to reveal, so a
 * picker whose list is entirely live does not carry a control that does
 * nothing.
 */
const actionsFor = (text, hasArchived) => [
  { key: ADD_ITEM, icon: 'add', label: 'Add item', hint: 'Create a new inventory item' },
  { key: PREVIOUS, label: text.toggle, hint: text.toggleHint, toggle: true },
  ...(hasArchived
    ? [{ key: ARCHIVED, label: 'Show archived', hint: 'Include retired catalogue items — normally hidden', toggle: true }]
    : []),
  { key: NO_ITEM, icon: 'block', label: 'No item', hint: 'A typed line with a description only' },
];

function OrderItemPicker({
  party = 'customer',
  value = null,
  displayCode = '',
  displayName = '',
  items = [],
  previousItems = [],
  contracts = [],
  spotPrices = [],
  milkPowderContracts = [],
  // Lines on the purchase orders a bill is linked to: [{ product_code, product_name, quantity }]
  linkedLines = [],
  deliveryDate = null,
  periodName = null,
  disabled = false,
  onSelect,
  onAddItem,
  // (row, item) => take this row off the "Recently ordered" / "Recently
  // bought" list for everyone (the screen does it, and offers Undo). Without
  // it the rows have no X.
  onRemovePrevious = null,
}) {
  const text = PARTY_TEXT[party] || PARTY_TEXT.customer;
  /*
   * Archived items are kept out of the list until asked for. They are a large
   * and growing share of the catalogue — a seasonal blend is archived the
   * moment its season ends — and an archived blend's name reads exactly like a
   * live one, so listing them plainly both buries the wanted item and invites
   * picking the wrong one. Same rule as SearchableDropdown; see the block
   * there.
   */
  const [showArchived, setShowArchived] = useState(false);
  const hasArchived = useMemo(() => (items || []).some(i => i && i.is_archived), [items]);
  const ACTIONS = useMemo(() => actionsFor(text, hasArchived), [text, hasArchived]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  // All inventory by default. The toggle narrows to what this customer has
  // bought before — kept per row while the row exists, so adding the same kind
  // of line twice does not mean flipping it back on each time.
  const [previousOnly, setPreviousOnly] = useState(false);
  const uid = useId();
  const toggleId = `oip-previous-${uid}`;
  const archivedToggleId = `oip-archived-${uid}`;
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const triggerRef = useRef(null);
  // Set when the menu closes from the keyboard, so focus goes back to the
  // trigger once it has re-rendered — otherwise closing with Enter or Escape
  // drops focus onto the page and the next keystroke goes nowhere.
  const refocusRef = useRef(false);
  const listRef = useRef(null);

  // Measured against the page area rather than the grid, which carries
  // overflow-x for its horizontal scroll and is only as tall as the table — so
  // the flip decision would be made against a few pixels of space.
  const { menuStyle } = useDropdownPosition(containerRef, open, {
    escapeOverflow: true,
    reflowOnScroll: true,
    boundarySelector: '.content-inner',
  });

  // Previous items keep the server's most-recent-first order, and are drawn
  // only from `items` — this customer's own list — so an item hidden from them
  // (another farm's bespoke blend) cannot come back through their history.
  const source = useMemo(() => {
    // The line's OWN item stays listed whatever its state — an older document
    // legitimately names an item that has since been archived, and hiding it
    // would blank the picker on a line nobody has touched.
    const visible = (list) => (showArchived
      ? list
      : list.filter(i => !i?.is_archived || i?.id === value));
    if (!previousOnly) return visible(items);
    const byId = new Map(items.map(i => [i.id, i]));
    return visible(previousItems.map(p => byId.get(p.inventory_item_id)).filter(Boolean));
  }, [previousOnly, items, previousItems, showArchived, value]);

  const badgesFor = useCallback((row) => pricingBadgesFor({
    itemId: row.id ?? row.inventory_item_id,
    productCode: itemCodeOf(row),
    productName: itemNameOf(row),
    contracts,
    spotPrices,
    milkPowderContracts,
    deliveryDate,
    periodName,
  }), [contracts, spotPrices, milkPowderContracts, deliveryDate, periodName]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return source;
    return source.filter(i =>
      `${itemCodeOf(i)} ${itemNameOf(i)}`.toLowerCase().includes(q)
    );
  }, [source, query]);

  // Actions first, then the filtered items — one flat list so the arrow keys
  // move through exactly what is on screen, in the order it appears.
  const navigable = useMemo(
    () => [...ACTIONS.map(a => ({ kind: 'action', ...a })), ...filtered.map(i => ({ kind: 'item', item: i }))],
    [ACTIONS, filtered]
  );

  useEffect(() => { setCursor(0); }, [query, previousOnly, showArchived]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const el = searchRef.current;
        if (!el) return;
        el.focus();
        // Opened by typing: the first character is already in the query, and
        // the caret must follow it or the second character lands before it.
        const end = el.value.length;
        el.setSelectionRange(end, end);
      }, 0);
    } else {
      setQuery('');
      if (refocusRef.current) {
        refocusRef.current = false;
        triggerRef.current?.focus();
      }
    }
  }, [open]);

  const closeFromKeyboard = () => { refocusRef.current = true; setOpen(false); };

  // Keep the highlighted row in view as the arrows walk past the fold.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector('[data-cursor="true"]');
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  const choose = useCallback((entry) => {
    if (!entry) return;
    if (entry.kind === 'action') {
      if (entry.key === ADD_ITEM) { setOpen(false); onAddItem?.(); return; }
      if (entry.key === PREVIOUS) { setPreviousOnly(v => !v); searchRef.current?.focus(); return; }
      if (entry.key === ARCHIVED) { setShowArchived(v => !v); searchRef.current?.focus(); return; }
      if (entry.key === NO_ITEM) { setOpen(false); onSelect?.(null); return; }
      return;
    }
    setOpen(false);
    onSelect?.(entry.item);
  }, [onAddItem, onSelect]);

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setOpen(true);
        return;
      }
      // Type straight into the cell. A printable key on the closed trigger
      // opens the picker with that character already searched for — there is
      // no separate search box to click into first.
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setQuery(e.key);
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); closeFromKeyboard(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor(c => Math.min(c + 1, navigable.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor(c => Math.max(c - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const entry = navigable[cursor];
      // The toggle keeps the picker open; everything else closes it.
      if (!(entry?.kind === 'action' && entry.key === PREVIOUS)) refocusRef.current = true;
      choose(entry);
    }
  };

  // Tabbing out of the search closes the picker. Deferred a tick so focus has
  // already landed on the next control — closing synchronously would unmount
  // the input mid-Tab and send focus back to the top of the page. Clicks inside
  // the menu never blur it: the menu swallows mousedown (see below).
  const onSearchBlur = () => {
    setTimeout(() => {
      if (containerRef.current && !containerRef.current.contains(document.activeElement)) setOpen(false);
    }, 0);
  };

  // Contracted items, split by whether the contract actually covers the
  // delivery date — the same rule that prices the line, so the panel cannot
  // offer a contract the grid then declines to use. Splitting on the period
  // name alone was wrong twice over: a straights row is one month of a
  // six-month period, and a customer often holds two contracts for one product
  // inside a single period, split by date.
  //
  // The rest still matter — they are what this customer buys — but they are not
  // what applies today, so they read as secondary.
  const { inPeriod, outOfPeriod } = useMemo(() => {
    const a = [];
    const b = [];
    const live = new Set();
    const seen = new Set();
    // A product with and without an additive are two contracts at two prices,
    // so each additive version is its own row.
    const liveItems = new Set();
    for (const c of contracts) {
      if (c.is_archived) continue;
      // A holding contract has no item yet — nothing here can be picked from it.
      if (c.product_id == null && !c.product_code) continue;
      if (!contractAppliesOn(c, { deliveryDate, periodName })) continue;
      const item = `${c.product_id ?? c.product_code}`;
      const version = `${item}|${additiveSignature(c.additives)}`;
      if (live.has(version)) continue;
      live.add(version);
      liveItems.add(item);
      a.push(c);
    }
    for (const c of contracts) {
      if (c.is_archived) continue;
      if (c.product_id == null && !c.product_code) continue;
      const item = `${c.product_id ?? c.product_code}`;
      // Already offered above — don't list the same product twice.
      if (liveItems.has(item)) continue;
      const key = `${item}|${additiveSignature(c.additives)}-${c.period_name || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      b.push(c);
    }
    return { inPeriod: a, outOfPeriod: b };
  }, [contracts, periodName, deliveryDate]);

  // What this customer last bought, from their own invoices and orders, looked
  // up by account id. It used to come from a free-text purchase-order search on
  // the account number, which matched that number anywhere in any PO — account
  // 0081 was shown another farm's item because that farm's PO carried the
  // reference SO_1008119. Only items in this customer's list are shown. An
  // archived one is listed — it is what they bought — but faded, badged and not
  // choosable (Cam, 28 Oct 2026; the server says `is_archived` too).
  const recent = useMemo(() => {
    const byId = new Map(items.map(i => [i.id, i]));
    return previousItems
      .map(p => ({ ...p, item: byId.get(p.inventory_item_id) }))
      .filter(r => r.item)
      .slice(0, 12)
      .map(r => ({ ...r, state: pickerRowState({ item: r.item, flagged: !!r.is_archived, value }) }));
  }, [items, previousItems, value]);

  // Looks in the customer's whole list, not the toggled view: a contract row in
  // the side panel must still be pickable while "previous items" is on.
  // A contract row passes itself along, so the line takes THAT contract's price
  // and additives rather than whichever version of the product ranks first.
  const pickItem = (match, contractRow = null) => {
    if (!match) return;
    setOpen(false);
    onSelect?.(match, contractRow ? { contractKey: contractKeyOf(contractRow) } : undefined);
  };
  // The item a contract or linked-PO row stands for, and how it behaves.
  const sideItem = (row, opts = {}) => {
    const item = itemForPickerRow(items, row);
    return { item, state: pickerRowState({ item, value, ...opts }) };
  };

  return (
    <div className="order-item-picker" ref={containerRef}>
      {open ? (
        // The search lives in the trigger. Clicking the cell or typing into it
        // lands here, so there is nothing further to click before typing.
        <div
          className="oip-trigger is-open"
          onMouseDown={(e) => { if (e.target !== searchRef.current) e.preventDefault(); }}
        >
          <input
            ref={searchRef}
            className="oip-trigger-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={onSearchBlur}
            placeholder={previousOnly ? text.searchPrevious : 'Search all inventory…'}
            aria-label="Search items"
            autoComplete="off"
            spellCheck={false}
          />
          <button
            type="button"
            className="oip-caret-btn"
            tabIndex={-1}
            onClick={() => closeFromKeyboard()}
            title="Close"
          >
            <span className="material-symbols-rounded oip-caret">expand_less</span>
          </button>
        </div>
      ) : (
      <button
        ref={triggerRef}
        type="button"
        className={`oip-trigger ${open ? 'is-open' : ''}`}
        onClick={() => !disabled && setOpen(o => !o)}
        onKeyDown={onKeyDown}
        disabled={disabled}
        title={[displayCode, displayName].filter(Boolean).join(' ') || 'Choose an item'}
      >
        {displayCode && <span className="oip-code-badge">{displayCode}</span>}
        <span className={`oip-trigger-text ${!displayCode && !displayName ? 'is-placeholder' : ''}`}>
          {displayName || (displayCode ? '' : 'Choose an item…')}
        </span>
        <span className="material-symbols-rounded oip-caret">expand_more</span>
      </button>
      )}

      {/* Hidden for the one frame before useDropdownPosition has measured. The
          CSS pins this fixed regardless, so it never takes part in the layout
          and opening it cannot move anything — but without the visibility guard
          it would flash at the unpositioned coordinates first. */}
      {open && (
        <div
          className="oip-menu"
          style={{
            ...(menuStyle || {}),
            // The hook aligns this to the trigger's left edge with a 4px gap,
            // like every other dropdown; only the width differs, because the
            // side panel needs room. Clamped so it is sane on a laptop and
            // uses the space on a 27" without running the full width.
            width: 'clamp(560px, 48vw, 900px)',
            minWidth: 0,
            visibility: menuStyle ? 'visible' : 'hidden',
          }}
          // No keydown of its own: the search input used to sit inside the menu,
          // so every arrow key was handled twice. Mousedown is swallowed so a
          // click in here never takes focus away from the search.
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="oip-left">
            <div className="oip-actions">
              {ACTIONS.map((a, idx) => (a.toggle ? (
                // A row, not a bare switch, so the whole width is a target and
                // the arrow keys land on it like any other action. Clicks on the
                // switch itself go to the input and stop there, so the row's own
                // handler does not flip it straight back.
                <div
                  key={a.key}
                  className={`oip-action oip-toggle ${cursor === idx ? 'is-cursor' : ''} ${(a.key === ARCHIVED ? showArchived : previousOnly) ? 'is-on' : ''}`}
                  data-cursor={cursor === idx ? 'true' : undefined}
                  onMouseEnter={() => setCursor(idx)}
                  onClick={() => choose({ kind: 'action', ...a })}
                  title={a.hint}
                >
                  <span className="oip-toggle-label">{a.label}</span>
                  <div className="checkbox-container" onClick={(e) => e.stopPropagation()}>
                    <div className="check light">
                      <input
                        id={a.key === ARCHIVED ? archivedToggleId : toggleId}
                        type="checkbox"
                        checked={a.key === ARCHIVED ? showArchived : previousOnly}
                        onChange={(e) => (a.key === ARCHIVED ? setShowArchived : setPreviousOnly)(e.target.checked)}
                      />
                      <label htmlFor={a.key === ARCHIVED ? archivedToggleId : toggleId}></label>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  key={a.key}
                  type="button"
                  className={`oip-action ${cursor === idx ? 'is-cursor' : ''}`}
                  data-cursor={cursor === idx ? 'true' : undefined}
                  onMouseEnter={() => setCursor(idx)}
                  onClick={() => choose({ kind: 'action', ...a })}
                  title={a.hint}
                >
                  <span className="material-symbols-rounded">{a.icon}</span>
                  <span>{a.label}</span>
                </button>
              )))}
            </div>

            <div className="oip-list" ref={listRef}>
              {filtered.length === 0 && (
                <div className="oip-empty">
                  {query
                    ? 'Nothing matches that.'
                    : (previousOnly ? text.noPrevious : 'No items to show.')}
                  {previousOnly && (
                    <button type="button" className="oip-inline-link" onClick={() => setPreviousOnly(false)}>
                      Search all inventory instead
                    </button>
                  )}
                </div>
              )}
              {filtered.map((i, n) => {
                const idx = ACTIONS.length + n;
                const b = badgesFor(i);
                return (
                  <button
                    key={i.id}
                    type="button"
                    className={`oip-item ${cursor === idx ? 'is-cursor' : ''} ${value === i.id ? 'is-selected' : ''} ${i.is_archived ? 'is-archived' : ''}`.trim()}
                    data-cursor={cursor === idx ? 'true' : undefined}
                    onMouseEnter={() => setCursor(idx)}
                    onClick={() => choose({ kind: 'item', item: i })}
                  >
                    {itemCodeOf(i) && <span className="oip-code-badge">{itemCodeOf(i)}</span>}
                    {/* The status sits inside the name, 5px after it, so it
                        reads as part of the name and never drops below it.
                        Only when it is NOT simply in use — ItemStatusBadge
                        says "Active" otherwise, and a badge on every row of a
                        long list says nothing at all. */}
                    <span className="oip-item-name">
                      <span className="oip-item-name-text">{itemNameOf(i)}</span>
                      {(i.is_archived || i.is_locally_archived || i.awaiting_supplier_code) && (
                        <ItemStatusBadge item={i} className="oip-item-status" />
                      )}
                    </span>
                    <span className="oip-item-badges">
                      {b.hasSpot && (
                        <span className="material-symbols-rounded oip-ic spot" title="Spot price for this customer on the delivery date">sell</span>
                      )}
                      {b.isDeccox && (
                        <span className="material-symbols-rounded oip-ic med" title="Medicated — needs a prescription">medication</span>
                      )}
                      {b.hasContract && (
                        <span className="material-symbols-rounded oip-ic con" title="Contract price for this customer in the delivery date’s period">handshake</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Scrolls on its own: what this customer (or supplier) actually
              trades, beside what is available. */}
          <div className="oip-right">
            {party === 'supplier' && linkedLines.length > 0 && (
              <section>
                <h4>On linked POs</h4>
                {linkedLines.map((l, idx) => {
                  // A bill bills what its PO bought — an item archived since
                  // is still the one to name — so these stay choosable.
                  const { item, state } = sideItem(l, { allowArchived: true });
                  const pickable = !!l.product_code && !!item;
                  return (
                    <SideRow
                      key={`linked-${idx}`}
                      state={state}
                      disabled={!pickable}
                      onPick={() => pickItem(item)}
                      title={pickable ? undefined : 'Not an inventory item — type it as a description instead'}
                    >
                      {l.product_code && <span className="oip-code-badge">{l.product_code}</span>}
                      <SideName name={l.product_name} item={item} archived={state.archived} />
                      {l.quantity != null && <span className="oip-side-period">× {formatQuantity(l.quantity)}</span>}
                    </SideRow>
                  );
                })}
              </section>
            )}

            <section>
              <h4>{text.recent}</h4>
              {recent.length === 0 && <p className="oip-side-empty">{text.recentEmpty}</p>}
              {recent.map((r) => {
                const row = (
                  <SideRow
                    key={r.inventory_item_id}
                    state={r.state}
                    onPick={() => pickItem(r.item)}
                    title={`Last bought ${formatDateShort(r.last_bought)} · ${r.times} time${r.times === 1 ? '' : 's'}`}
                  >
                    {itemCodeOf(r.item) && <span className="oip-code-badge">{itemCodeOf(r.item)}</span>}
                    <SideName name={itemNameOf(r.item)} item={r.item} archived={r.state.archived} />
                    <span className="oip-side-period">{formatDateShort(r.last_bought)}</span>
                  </SideRow>
                );
                if (!onRemovePrevious) return row;
                // The line's own item keeps the X's space, empty, so the dates
                // stay in one column.
                const own = value != null && r.inventory_item_id === value;
                const block = r.remove_block || null;
                return (
                  <div key={r.inventory_item_id} className="oip-side-line">
                    {row}
                    {own ? (
                      <span className="oip-side-remove-space" aria-hidden="true" />
                    ) : (
                      <button
                        type="button"
                        className="btn-icon btn-danger oip-side-remove"
                        disabled={!!block}
                        title={block || REMOVE_TITLE}
                        aria-label={`Remove ${[itemCodeOf(r.item), itemNameOf(r.item)].filter(Boolean).join(' ')} from ${text.recent.toLowerCase()}`}
                        onClick={(e) => { e.stopPropagation(); if (!block) onRemovePrevious(r, r.item); }}
                      >
                        <span className="material-symbols-rounded">close</span>
                      </button>
                    )}
                  </div>
                );
              })}
            </section>

            {party === 'customer' && (
            <section>
              <h4>Contracted{periodName ? ` — ${periodName}` : ''}</h4>
              {inPeriod.length === 0 && <p className="oip-side-empty">No contract for this period.</p>}
              {inPeriod.map((c) => {
                const { item, state } = sideItem(c);
                return (
                  <SideRow key={`in-${contractKeyOf(c)}`} state={state} onPick={() => pickItem(item, c)}>
                    {c.product_code && <span className="oip-code-badge">{c.product_code}</span>}
                    <SideName name={withAdditives(c.product_name, c.additives)} item={item} archived={state.archived} />
                  </SideRow>
                );
              })}
            </section>
            )}

            {party === 'customer' && outOfPeriod.length > 0 && (
              <section className="oip-dim">
                <h4>Contracted — other periods</h4>
                {outOfPeriod.map((c) => {
                  const { item, state } = sideItem(c);
                  return (
                    <SideRow key={`out-${contractKeyOf(c)}`} state={state} onPick={() => pickItem(item)}
                             title={`Contracted for ${c.period_name || 'another period'} — not for this delivery date`}>
                      {c.product_code && <span className="oip-code-badge">{c.product_code}</span>}
                      <SideName name={withAdditives(c.product_name, c.additives)} item={item} archived={state.archived} />
                      <span className="oip-side-period">{c.period_name}</span>
                    </SideRow>
                  );
                })}
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderItemPicker;
